import { use } from 'react'

/*
 * Route code, loaded once and then read synchronously.
 *
 * `React.lazy` suspends the first time each lazy component renders, even when
 * its chunk is already in memory, and a route that suspends inside a freshly
 * mounted Suspense boundary shows that boundary's blank fallback. That was the
 * white flash between pages and on every page load. Here a route's code is
 * loaded ahead (by its route loader, or on hover) and the page reads it from
 * this cache in the same render, so a ready page never suspends.
 *
 * `useRouteModule` still suspends, through `use`, if something renders before its code
 * has arrived (browser back/forward to a page never visited), so the old
 * fallbacks remain the safety net rather than the normal path.
 */
type Entry<T> = { promise: Promise<T>; value?: T }

const entries = new Map<string, Entry<unknown>>()

export function preload<T>(key: string, loader: () => Promise<T>): Promise<T> {
  const hit = entries.get(key) as Entry<T> | undefined
  if (hit) return hit.promise
  const entry = {} as Entry<T>
  entry.promise = loader().then(
    (value) => {
      entry.value = value
      return value
    },
    (error: unknown) => {
      // Forget a failed load so the next attempt fetches again.
      entries.delete(key)
      throw error
    },
  )
  entries.set(key, entry as Entry<unknown>)
  return entry.promise
}

export function useRouteModule<T>(key: string, loader: () => Promise<T>): T {
  const hit = entries.get(key) as Entry<T> | undefined
  if (hit && hit.value !== undefined) return hit.value
  return use(preload(key, loader))
}
